// FormCreator.tsx
import React, { useState, useRef } from "react";
import { UseFormReturn, FormProvider } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MultiSelect } from "@/components/shared/inputs/multi-select.tsx";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Button } from "@/components/ui/button";
import { FormField, FormItem, FormLabel, FormControl, FormDescription, FormMessage } from "@/components/ui/form";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { FileUpload } from "../inputs/file-upload";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Check, ChevronsUpDown, Eye, EyeOff } from "lucide-react";
import { format } from "date-fns";
import { SearchBox as SB } from "@mapbox/search-js-react";
import { $FIXME } from "@/interfaces";

const SearchBox: $FIXME = SB;
export interface FormFieldDef {
  type:
  | "text"
  | "number"
  | "email"
  | "password"
  | "textarea"
  | "select"
  | "combobox"
  | "checkbox"
  | "switch"
  | "radio"
  | "date"
  | "file"
  | "address"
  | "section";
  name: string;
  label: string;
  labelRight?: string | React.ReactNode;
  placeholder?: string;
  description?: string;
  options?: { label: string; value: string }[];
  /** Static disabled state */
  disabled?: boolean;
  /** Dynamic disabled state based on form values */
  disabledWhen?: (values: any) => boolean;
  required?: boolean;
  multiple?: boolean;
  accept?: string;
  wrapperClassName?: string;
  inputClassName?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  hidden?: boolean | ((values: any) => boolean);
  minDate?: Date | (() => Date);
  isDisabledDay?: (day: Date) => boolean;
  /** Render a custom element alongside the field (e.g., Add Unit button) */
  addonRight?: React.ReactNode | ((values: any) => React.ReactNode);
  /** For combobox: placeholder in search input (defaults to "Search...") */
  searchPlaceholder?: string;
}

export interface FormCreatorProps {
  form: UseFormReturn<any>;
  onSubmit: (values: any) => void;
  formDefinition: FormFieldDef[];
  buttonText?: string;
  resetButtonText?: string;
  formClassName?: string;
  /** Hide the default submit/reset buttons */
  hideButtons?: boolean;
  /** Render custom buttons instead of default */
  renderButtons?: (form: UseFormReturn<any>) => React.ReactNode;
}

export function FormCreator({
  form,
  onSubmit,
  formDefinition,
  buttonText,
  resetButtonText,
  formClassName,
  hideButtons,
  renderButtons,
}: FormCreatorProps) {
  const [datePopoverOpen, setDatePopoverOpen] = useState<Record<string, boolean>>({});
  const [comboboxOpen, setComboboxOpen] = useState<Record<string, boolean>>({});
  const [passwordVisible, setPasswordVisible] = useState<Record<string, boolean>>({});
  const formValues = form.watch();
  const containerRef = useRef<HTMLDivElement>(null);

  const getDisabledState = (field: FormFieldDef): boolean => {
    if (field.disabled) return true;
    if (field.disabledWhen) return field.disabledWhen(formValues);
    return false;
  };

  const getMinDate = (field: FormFieldDef): Date | undefined => {
    if (!field.minDate) return undefined;
    return typeof field.minDate === "function" ? field.minDate() : field.minDate;
  };

  return (
    <FormProvider {...form}>
      <div ref={containerRef}>
        <form onSubmit={form.handleSubmit(onSubmit)} className={cn(formClassName ?? "flex flex-col gap-4")}>
          {formDefinition
            .filter((field) => {
              if (typeof field.hidden === "function") {
                return !field.hidden(formValues);
              }
              return !field.hidden;
            })
            .map((field) => {
              // Handle section headers
              if (field.type === "section") {
                return (
                  <h4 key={field.name} className={cn("font-medium text-sm text-muted-foreground", field.wrapperClassName)}>
                    {field.label}
                  </h4>
                );
              }

              const isDisabled = getDisabledState(field);
              const minDate = getMinDate(field);
              const addonRight = typeof field.addonRight === "function"
                ? field.addonRight(formValues)
                : field.addonRight;

              return (
                <FormField
                  key={field.name}
                  control={form.control}
                  name={field.name}
                  render={({ field: rhfField }) => (
                    <FormItem className={cn(field.wrapperClassName)}>
                      {field.label && (
                        <FormLabel>
                          <div className="w-full flex items-center justify-between">
                            <span>
                              {field.label} {field.required && <span className="text-red-500">*</span>}
                            </span>
                            {field.labelRight && typeof field.labelRight === "string" ? (
                              <span className="text-sm text-muted-foreground">{field.labelRight}</span>
                            ) : (
                              field.labelRight
                            )}
                          </div>
                        </FormLabel>
                      )}
                      <div className={cn(addonRight && "flex gap-2")}>
                        <FormControl className={cn(addonRight && "flex-1")}>
                          {(() => {
                            switch (field.type) {
                              case "textarea":
                                return (
                                  <Textarea
                                    {...rhfField}
                                    placeholder={field.placeholder}
                                    className={cn(field.inputClassName)}
                                    disabled={isDisabled}
                                  />
                                );
                              case "select":
                                if (field.multiple) {
                                  return (
                                    <MultiSelect
                                      options={field.options ?? []}
                                      value={rhfField.value ?? []}
                                      onChange={rhfField.onChange}
                                    />
                                  );
                                }

                                return (
                                  <Select
                                    onValueChange={rhfField.onChange}
                                    value={rhfField.value || ""}
                                    disabled={isDisabled}
                                  >
                                    <SelectTrigger className={cn("w-full", field.inputClassName)}>
                                      <SelectValue placeholder={field.placeholder} />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {field.options?.map((opt) => (
                                        <SelectItem key={opt.value} value={opt.value}>
                                          {opt.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                );
                              case "combobox": {
                                const options = field.options ?? [];
                                const selected = options.find((o) => o.value === rhfField.value);
                                const open = comboboxOpen[field.name] || false;
                                const searchPh = field.searchPlaceholder ?? `Search ${field.placeholder || field.label}...`;
                                return (
                                  <Popover open={open} onOpenChange={(o) => setComboboxOpen((prev) => ({ ...prev, [field.name]: o }))}>
                                    <PopoverTrigger asChild>
                                      <Button
                                        type="button"
                                        variant="outline"
                                        role="combobox"
                                        aria-expanded={open}
                                        disabled={isDisabled}
                                        className={cn(
                                          addonRight ? "flex-1" : "w-full",
                                          "justify-between font-normal",
                                          !rhfField.value && "text-muted-foreground",
                                          field.inputClassName
                                        )}
                                      >
                                        {selected ? selected.label : field.placeholder}
                                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                      </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[var(--radix-popover-trigger-width)] p-0" align="start" container={containerRef.current}>
                                      <Command>
                                        <CommandInput placeholder={searchPh} />
                                        <CommandList>
                                          <CommandEmpty>No results found.</CommandEmpty>
                                          <CommandGroup>
                                            {options.map((opt) => (
                                              <CommandItem
                                                key={opt.value}
                                                value={opt.label}
                                                onSelect={() => {
                                                  rhfField.onChange(opt.value === rhfField.value ? "" : opt.value);
                                                  setComboboxOpen((prev) => ({ ...prev, [field.name]: false }));
                                                }}
                                              >
                                                <Check className={cn("mr-2 h-4 w-4", rhfField.value === opt.value ? "opacity-100" : "opacity-0")} />
                                                {opt.label}
                                              </CommandItem>
                                            ))}
                                          </CommandGroup>
                                        </CommandList>
                                      </Command>
                                    </PopoverContent>
                                  </Popover>
                                );
                              }
                              case "checkbox":
                                return (
                                  <Checkbox
                                    checked={rhfField.value}
                                    onCheckedChange={rhfField.onChange}
                                    disabled={isDisabled}
                                  />
                                );
                              case "switch":
                                return (
                                  <Switch
                                    checked={rhfField.value}
                                    onCheckedChange={rhfField.onChange}
                                    disabled={isDisabled}
                                  />
                                );
                              case "radio":
                                return (
                                  <RadioGroup value={rhfField.value} onValueChange={rhfField.onChange} className="flex gap-4">
                                    {field.options?.map((opt) => (
                                      <FormItem key={opt.value} className="flex items-center space-x-2">
                                        <FormControl>
                                          <RadioGroupItem value={opt.value} disabled={isDisabled} />
                                        </FormControl>
                                        <FormLabel className="!m-0">{opt.label}</FormLabel>
                                      </FormItem>
                                    ))}
                                  </RadioGroup>
                                );
                              case "date":
                                return (
                                  <Popover
                                    open={datePopoverOpen[field.name] || false}
                                    onOpenChange={(open) => setDatePopoverOpen(prev => ({ ...prev, [field.name]: open }))}
                                  >
                                    <PopoverTrigger asChild>
                                      <Button
                                        type="button"
                                        variant="outline"
                                        className={cn(
                                          "w-full justify-start text-left font-normal",
                                          !rhfField.value && "text-muted-foreground"
                                        )}
                                        disabled={isDisabled}
                                      >
                                        {rhfField.value ? format(rhfField.value, "PPP") : field.placeholder || "Pick a date"}
                                      </Button>
                                    </PopoverTrigger>
                                    <PopoverContent
                                      className="w-auto p-0"
                                      align="start"
                                      container={containerRef.current}
                                    >
                                      <Calendar
                                        mode="single"
                                        selected={rhfField.value}
                                        onSelect={(date) => {
                                          rhfField.onChange(date);
                                          setDatePopoverOpen(prev => ({ ...prev, [field.name]: false }));
                                        }}
                                        disabled={(date) => (minDate ? date < minDate : false) || (field.isDisabledDay?.(date) ?? false)}
                                      />
                                    </PopoverContent>
                                  </Popover>
                                );
                              case "file":
                                return (
                                  <FileUpload
                                    name={field.name}
                                    label={undefined}
                                    form={form}
                                    accept={field.accept}
                                    prompt={field.placeholder}
                                  />
                                );
                              case "password": {
                                const visible = passwordVisible[field.name] ?? false;
                                return (
                                  <div className="relative">
                                    <Input
                                      type={visible ? "text" : "password"}
                                      placeholder={field.placeholder}
                                      {...rhfField}
                                      className={cn("pr-9", field.inputClassName)}
                                      disabled={isDisabled}
                                    />
                                    <button
                                      type="button"
                                      tabIndex={-1}
                                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-sm"
                                      onClick={() =>
                                        setPasswordVisible((prev) => ({ ...prev, [field.name]: !prev[field.name] }))
                                      }
                                      aria-label={visible ? "Hide password" : "Show password"}
                                    >
                                      {visible ? (
                                        <EyeOff className="h-4 w-4" />
                                      ) : (
                                        <Eye className="h-4 w-4" />
                                      )}
                                    </button>
                                  </div>
                                );
                              }
                              case "address":
                                return (
                                  <SearchBox
                                    accessToken={import.meta.env.VITE_MAPBOX_KEY}
                                    options={{
                                      language: "en",
                                      country: "US",
                                      types: "address",
                                    }}
                                    onRetrieve={(val: any) => {
                                      const fullAddress = val.features?.[0]?.properties?.full_address ?? "";
                                      rhfField.onChange(fullAddress);
                                    }}
                                    value={rhfField.value}
                                    theme={{
                                      variables: {},
                                      icons: {
                                        search: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-search-icon lucide-search"><path d="m21 21-4.34-4.34"/><circle cx="11" cy="11" r="8"/></svg>`,
                                        close: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-x-icon lucide-circle-x"><circle cx="12" cy="12" r="10"/><path d="m15 9-6 6"/><path d="m9 9 6 6"/></svg>`,
                                      },
                                    }}
                                  />
                                );
                              default:
                                return (
                                  <Input
                                    type={field.type}
                                    placeholder={field.placeholder}
                                    {...rhfField}
                                    className={cn(field.inputClassName)}
                                    disabled={isDisabled}
                                  />
                                );
                            }
                          })()}
                        </FormControl>
                        {addonRight}
                      </div>
                      {field.description && <FormDescription>{field.description}</FormDescription>}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              );
            })}
          {renderButtons ? (
            renderButtons(form)
          ) : !hideButtons ? (
            <div className="flex items-center justify-between gap-4">
              {resetButtonText && (
                <Button className="flex-1" type="reset" variant={"secondary"} onClick={() => form.reset()}>
                  {resetButtonText || "Reset"}
                </Button>
              )}
              <Button className="flex-1" type="submit">
                {buttonText || "Submit Form"}
              </Button>
            </div>
          ) : null}
        </form>
      </div>
    </FormProvider>
  );
}
